import React, {useRef} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {Pill} from '@vio/atlas/Pill'
import {Popover, PopoverPlacement} from '@vio/atlas/Popover'
import useBodyScrollLock from '@vio/atlas/src/components/designSystem/Overlay/useBodyScrollLock'
import {cssTheme} from '@vio/atlas/themes'

import {FilterDisplayContent} from './FilterDisplayContent'

const StyledPill = styled(Pill)`
  outline-color: ${cssTheme.colors.border.interactive.primary}!important;
  color: ${cssTheme.colors.content.interactive.primary}!important;
  svg > path {
    fill: ${cssTheme.colors.content.interactive.primary};
  }
  &.--selected {
    color: ${cssTheme.colors.pill.selected.content}!important;
    svg > path {
      fill: ${cssTheme.colors.pill.selected.content};
    }
  }
  &.--selected {
    background-color: ${cssTheme.colors.pill.selected.background} !important;
  }
`

export interface FilterDisplayContainerProps {
  /** The pill label */
  label: React.ReactNode
  /** The header title of the popover / overlay */
  title: React.ReactNode
  /** The children to render within the popover / overlay */
  children: React.ReactNode
  /** The icon that should be rendered in the pill */
  icon?: React.ReactElement
  /** The count that should be rendered in the pill */
  appliedFiltersCount?: number
  /** The count of all filters that are selected but not applied */
  filtersCount?: number
  /** Callback fired on clear button click */
  onClear: () => void
  /** Callback fired on apply button click */
  onApply: () => void
  /** Callback fired when the popover / overlay closes */
  onClose?: () => void
  /** Callback fired on click of the pill */
  onPillClick?: () => void
  /** Element identification */
  dataId?: string
}

/**
 * FilterDisplayContainer is a visual component that allows you to pass a filter body to show inside a popover or overlay.
 *
 * It uses a FilterPill component as a trigger, and has built-in controls to Clear and Apply the values of the filter.
 * State for the form is not managed by this component, you should do that in your own component.
 */
export const FilterDisplayContainer: React.FC<FilterDisplayContainerProps> = ({
  appliedFiltersCount,
  filtersCount,
  label,
  title,
  children,
  icon,
  dataId = '',
  onClear,
  onApply,
  onClose,
  onPillClick
}) => {
  const pillRef = React.useRef<HTMLButtonElement>()
  const [isOpen, setIsOpen] = React.useState(false)

  const handleClear = React.useCallback(() => {
    onClear()
  }, [onClear])

  const handleOpenClose = React.useCallback(() => {
    setIsOpen(!isOpen)
    if (onClose && isOpen) onClose()
  }, [onClose, setIsOpen, isOpen])

  const handlePillClick = React.useCallback(() => {
    handleOpenClose()
    if (onPillClick) onPillClick()
  }, [handleOpenClose, onPillClick])

  const handleApply = React.useCallback(() => {
    onApply()
    setIsOpen(false)
  }, [onApply, setIsOpen])

  const handleOutsideClick = React.useCallback(
    (e: MouseEvent) => {
      /**
       * We close the Popover if we click anywhere except the Pill component itself
       */
      if (pillRef.current && !pillRef.current.contains(e.target as Node)) {
        handleOpenClose()
      }
    },
    [pillRef, handleOpenClose]
  )
  const containerRef = useRef<HTMLDivElement>(null)
  useBodyScrollLock({ref: containerRef, isEnabled: isOpen})

  const isSelected = appliedFiltersCount > 0

  return (
    <div ref={containerRef}>
      <StyledPill
        ref={pillRef}
        isSelected={isSelected}
        dataId={dataId}
        iconStart={icon}
        showArrow
        count={appliedFiltersCount}
        onClick={handlePillClick}
        className={cx(
          isOpen && !isSelected && '--active',
          isSelected && !isOpen && '--selected'
        )}
      >
        {label}
      </StyledPill>
      <Popover
        isOpen={isOpen}
        placement={PopoverPlacement.BottomLeft}
        anchorRef={pillRef}
        verticalOffset={16}
        onOutsideClick={handleOutsideClick}
        dataId={`popover-${dataId}`}
      >
        <FilterDisplayContent
          dataId={dataId}
          title={title || label}
          onClose={handleOpenClose}
          onClear={handleClear}
          onApply={handleApply}
          filtersCount={filtersCount}
        >
          {children}
        </FilterDisplayContent>
      </Popover>
    </div>
  )
}
